import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { markRowEnds } from './fit';
import { formatCompactRange } from './format';
import { PhoneFilledIcon } from './icons';
import OpsContactPopover from './OpsContactPopover';
import {
  CallButton,
  Dot,
  Item,
  List,
  MORE_POPOVER_CSS,
  MoreButton,
  MoreCard,
  MoreItem,
  Name,
  Time,
  Who,
} from './OpsOnDutyBar.styles';
import { FIT_ITEM_ATTR, FIT_MORE_ATTR, useFitCount } from './useFitCount';
import { useReturnFocus } from './useReturnFocus';

import Popover from '@/components/Popover';
import type { OnDutyEntry } from '@/services/opsRota/types';
import { trackOpsContactOpened, trackOpsMoreOpened } from '@/util/vexo';
import type { OpsBarSurface } from '@/util/vexo';

type Props = {
  entries: OnDutyEntry[];
  surface: OpsBarSurface;
  /** Show times in this zone, not the browser's. */
  timeZone?: string;
};

type Scope = 'bar' | 'more';

// Test ids follow the UI spec's selector map (§14). Inside the "+N more"
// list an entry renders a second time, so its ids carry a `More` prefix.
const tid = (scope: Scope, part: string, id: number) =>
  `opsOnDuty${scope === 'more' ? 'More' : ''}${part}-${id}`;

const keyOf = (scope: Scope, id: number) => `${scope}-${id}`;

type MoreListProps = {
  onClose: () => void;
  returnFocusTo: () => HTMLElement | null;
  focusFallback: () => HTMLElement | null;
  children: ReactNode;
};

// DB-05: "+N more" opens the full list. Its own component so the list can
// hand focus back to "+N more" when it closes.
const MoreList = ({
  onClose,
  returnFocusTo,
  focusFallback,
  children,
}: MoreListProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  useReturnFocus(cardRef, returnFocusTo, focusFallback);
  return (
    <Popover
      id="opsOnDutyMorePopover"
      isOpen
      onRequestClose={onClose}
      css={MORE_POPOVER_CSS}
    >
      <MoreCard
        ref={cardRef}
        role="dialog"
        aria-label="Everyone on the ops rota today"
        data-testid="opsOnDutyMoreList"
      >
        {children}
      </MoreCard>
    </Popover>
  );
};

const OpsOnDutyEntries = ({ entries, surface, timeZone }: Props) => {
  // Keyed by scope too, so one entry has one popover in either list. A
  // single key means only one popover is ever open (DB-13).
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  // Openers by popover key, so a card closing with focus inside can return
  // it to the button that opened it.
  const openers = useRef(new Map<string, HTMLButtonElement>());
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  // Where focus goes when its opener left with the card.
  const barRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    barRef.current = listRef.current?.closest('section') ?? null;
  }, []);
  const focusFallback = useCallback(
    () =>
      [moreButtonRef.current, barRef.current].find((el) => el?.isConnected) ??
      null,
    [],
  );
  // Remeasure whenever anything that changes an entry's width changes.
  const fitKey = entries
    .map(
      (e) => `${e.id}:${e.on_duty}:${e.first_name}:${e.starts_at}:${e.ends_at}`,
    )
    .concat(timeZone ?? '')
    .join(',');
  const { visible, settled, lastVisible } = useFitCount(
    listRef,
    entries.length,
    fitKey,
  );
  const hidden = entries.length - visible;
  // While a remeasure shows everyone, "+N more" keeps the last count.
  const moreCount = settled
    ? hidden
    : Math.max(0, entries.length - lastVisible);
  // No divider after the last entry of a wrapped row: re-marked after every
  // render (entries, OFF/on, "+N more") and on every resize of the list.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list) markRowEnds(list, ':scope > li');
  });
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => markRowEnds(list, ':scope > li'));
    observer.observe(list);
    return () => observer.disconnect();
  }, []);
  const closeContact = useCallback(() => setOpenKey(null), []);
  const closeMore = useCallback(() => {
    setMoreOpen(false);
    setOpenKey(null);
  }, []);

  const toggleContact =
    (key: string, entry: OnDutyEntry, scope: Scope) => (ev: MouseEvent) => {
      // Off duty opens nothing, and the click counts as an outside
      // click for whatever popover is open.
      if (!entry.on_duty) return;
      // Popover closes on any window click; the opening click would
      // otherwise reach window and close it at once.
      ev.stopPropagation();
      if (scope === 'bar') setMoreOpen(false);
      if (openKey !== key) {
        trackOpsContactOpened({
          surface,
          list: scope,
          has_phone: Boolean(entry.phone),
        });
      }
      setOpenKey((current) => (current === key ? null : key));
    };

  // A poll that takes the open contact off duty (or off the rota)
  // closes its popover; off duty never shows one.
  useEffect(() => {
    if (openKey === null) return;
    const stillOn = entries.some(
      (e) =>
        e.on_duty &&
        (openKey === keyOf('bar', e.id) || openKey === keyOf('more', e.id)),
    );
    if (!stillOn) setOpenKey(null);
  }, [entries, openKey]);

  // A resize that hides an entry into "+N more" closes its open card,
  // so widening again does not bring the card back on its own.
  useEffect(() => {
    if (!settled || !openKey?.startsWith('bar-')) return;
    const at = entries.findIndex((e) => keyOf('bar', e.id) === openKey);
    if (at >= visible) setOpenKey(null);
  }, [settled, visible, entries, openKey]);

  // Once everyone fits, "+N more" is gone: forget that its list was open,
  // or a later poll that hides someone again would reopen it. `settled`
  // skips the moment a remeasure shows everyone.
  useEffect(() => {
    if (!settled || hidden > 0) return;
    setMoreOpen(false);
    setOpenKey((key) => (key?.startsWith('more-') ? null : key));
  }, [settled, hidden]);

  const renderEntry = (entry: OnDutyEntry, scope: Scope = 'bar') => {
    const key = keyOf(scope, entry.id);
    return (
      <Item
        key={key}
        data-testid={tid(scope, 'Entry', entry.id)}
        {...(scope === 'bar' ? { [FIT_ITEM_ATTR]: '' } : {})}
      >
        <Who>
          <Dot
            on={entry.on_duty}
            aria-hidden="true"
            data-testid={tid(scope, 'Dot', entry.id)}
          />
          <Name>{entry.first_name || entry.name}</Name>
          <Time off={!entry.on_duty} data-testid={tid(scope, 'Time', entry.id)}>
            {entry.on_duty
              ? formatCompactRange(entry.starts_at, entry.ends_at, timeZone)
              : 'OFF'}
          </Time>
        </Who>
        <CallButton
          type="button"
          aria-label={`Show contact for ${entry.full_name}`}
          // Off duty opens nothing, so it announces no popup.
          aria-haspopup={entry.on_duty ? 'dialog' : undefined}
          aria-expanded={entry.on_duty ? openKey === key : undefined}
          aria-disabled={!entry.on_duty}
          data-testid={tid(scope, 'PhoneButton', entry.id)}
          ref={(el: HTMLButtonElement | null) => {
            if (el) openers.current.set(key, el);
            else openers.current.delete(key);
          }}
          onClick={toggleContact(key, entry, scope)}
        >
          <PhoneFilledIcon />
        </CallButton>
        {openKey === key && entry.on_duty && (
          <OpsContactPopover
            entry={entry}
            onClose={closeContact}
            stacked={scope === 'more'}
            surface={surface}
            timeZone={timeZone}
            returnFocusTo={() => openers.current.get(key) ?? null}
            focusFallback={focusFallback}
          />
        )}
      </Item>
    );
  };

  return (
    <List ref={listRef} data-testid="opsOnDutyList">
      {entries.slice(0, visible).map((entry) => renderEntry(entry))}
      {moreCount > 0 && (
        <MoreItem {...{ [FIT_MORE_ATTR]: '' }}>
          <MoreButton
            ref={moreButtonRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            data-testid="opsOnDutyMoreButton"
            onClick={(ev) => {
              ev.stopPropagation();
              setOpenKey(null);
              if (!moreOpen)
                trackOpsMoreOpened({ surface, hidden_count: moreCount });
              setMoreOpen((open) => !open);
            }}
          >
            +{moreCount} more
          </MoreButton>
          {moreOpen && (
            <MoreList
              onClose={closeMore}
              returnFocusTo={() => moreButtonRef.current}
              focusFallback={focusFallback}
            >
              <ul>{entries.map((entry) => renderEntry(entry, 'more'))}</ul>
            </MoreList>
          )}
        </MoreItem>
      )}
    </List>
  );
};

export default OpsOnDutyEntries;
